import { SlidingTabs } from "./SlidingTabs";
import {activeSceneMap} from '../runtime/activeSceneMap';
import { ModelPicker } from "./ModelPicker";
import { ActionReceipts, type ActionReceipt } from "./ActionReceipts";
import { MovisAvatar } from "./MovisAvatar";
import { normalizeVisibleNarration } from "@private-fiction/story-core";
import { StoryFileView } from "./StoryFileView";
import { normalizeStoryMap, playerVisibleDocuments, worldImageSource } from "@private-fiction/story-core";
import { useState, useRef, useEffect } from "react";
import type {
  WorldDocument,
  StoryMapDocument,
  AccountStoryDocument,
  ContextPacket,
  CompiledAgentContext,
  AgentActionRecord,
  WorldEvent,
} from "@private-fiction/story-core";
import { CharacterPanel } from "./CharacterPanel";
import { ControlRoomPanel } from "./ControlRoomPanel";
import { AccountMediaImage } from "./AccountMedia";
import { streamNarration } from "../runtime/gateway";
import { runtimeConfig } from "../config/runtimeConfig";
import type { RuntimeModel } from "../runtime/modelCatalog";
import type { SidebarConfig } from "./CustomizeSidebarModal";

export interface OrbitChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: number;
}

interface InChatRightDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab?: "overview" | "assistant";
  onTabChange?: (tab: "overview" | "assistant") => void;
  character?: (WorldDocument) | undefined;
  activeLocation?: (WorldDocument) | undefined;
  documents: readonly WorldDocument[];
  session: AccountStoryDocument | null;
  models: RuntimeModel[];
  selectedModel: string;
  onSelectModel: (model: RuntimeModel) => void;
  imageModel: string;
  imageEnabled: boolean;
  onSelectImageModel: (model: string) => void;
  onToggleImageGeneration: (enabled: boolean) => void;
  accessToken?: string;
  onRunCopilot?: (query: string, history: { role: "user" | "assistant"; content: string }[], onReceipt: (receipt: ActionReceipt) => void, signal?: AbortSignal) => Promise<string>;
  onSelectEntity?: (document: WorldDocument) => void;
  // Control Room props
  contextPacket?: ContextPacket | null;
  contextManifest?: CompiledAgentContext["manifest"] | null;
  agentActions?: AgentActionRecord[];
  worldEvents?: WorldEvent[];
  onUndoWorldTransaction?: (transactionId: string) => void;
  onUpdateSession?: (patch: Record<string, unknown>) => void;
  onSaveDefaults?: () => void;
  onUploadMedia?: (kind: "backgroundImageUri" | "chatIconImageUri" | "homeHeroImageUri", file: File) => Promise<void>;
  // Sidebar customization
  sidebarConfig: SidebarConfig;
  onOpenCustomizeModal: () => void;
}

// -----------------------------------------------------------------------------
// Modular Overview Sub-Components (Craft Parity)
// -----------------------------------------------------------------------------

export function SceneMapModule({
  map: activeMap,
  layerId,
  activeLocation,
  documents=[],
  onSelectEntity,
}: {
  map?: StoryMapDocument | undefined;
  layerId?:string;
  activeLocation?: WorldDocument | undefined;
  documents?:readonly WorldDocument[];
  onSelectEntity?: ((doc: WorldDocument) => void) | undefined;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [viewedMapId,setViewedMapId]=useState("");
  const readableMaps=playerVisibleDocuments(documents).filter(doc=>doc.kind==='story_map'&&!doc.deletedAt).map(doc=>normalizeStoryMap(doc.fields,doc.storyId));
  const map=readableMaps.find(item=>item.id===viewedMapId)||activeMap;
  const parent=readableMaps.find(item=>item.id===map?.parentMapId)||readableMaps.find(item=>item.id!==map?.id&&item.anchors.some(anchor=>anchor.targetMapId===map?.id||(map?.rootLocationId && anchor.locationDocumentId===map.rootLocationId)));
  useEffect(()=>setViewedMapId(""),[activeMap?.id]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [viewedLayerId,setViewedLayerId]=useState('');
  useEffect(()=>{setViewedLayerId('');setZoomLevel(1);},[map?.id,layerId]);
  const layer = map?.layers.find(item=>item.id===(viewedLayerId||layerId))||map?.layers[0];
  const visibleDocuments=playerVisibleDocuments(documents);
  const anchors=(map?.anchors??[]).filter(anchor=>anchor.layerId===layer?.id&&anchor.visibility!=='hidden');
  const openLocation=(id:string)=>{const file=visibleDocuments.find(file=>file.id===id)||(activeLocation?.id===id?activeLocation:undefined);if(file)onSelectEntity?.(file);};
  const mapImage = layer?.imageAssetId || "";
  const [imageSize, setImageSize] = useState({ source: '', ratio: 1 });
  const imageRatio = imageSize.source === mapImage ? imageSize.ratio : 1;
  const tokens = (map?.tokens ?? []).filter(token => token.layerId === layer?.id&&(!token.link||!documents.some(file=>file.id===token.link)||visibleDocuments.some(file=>file.id===token.link)));
  const areas=(map?.areas??[]).filter(area=>area.layerId===layer?.id&&(!area.locationDocumentId||anchors.some(anchor=>anchor.locationDocumentId===area.locationDocumentId)||activeLocation?.id===area.locationDocumentId));

  return (
    <div className={`craft-sidebar-module scene-map-module frosted-panel ${collapsed ? "collapsed" : ""}`}>
      <div className="module-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="module-header__title">
          <span className="module-icon">🗺</span>
          <strong>SCENE MAP</strong>
        </div>
        <div className="module-header__actions" onClick={(e) => e.stopPropagation()}>
          {activeLocation && onSelectEntity && (
            <button
              type="button"
              className="popout-btn"
              title="Open location details"
              onClick={() => onSelectEntity(activeLocation)}
            >
              ↗
            </button>
          )}
          <button
            type="button"
            className="collapse-toggle-btn"
            onClick={() => setCollapsed(!collapsed)}
            aria-label="Toggle collapse"
          >
            {collapsed ? "˅" : "˄"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="module-body scene-map-body">
          {(parent || viewedMapId)&&<nav className="scene-map-navigation" aria-label="แผนที่หลักและย่อย">{parent&&<button type="button" onClick={()=>setViewedMapId(parent.id)}>↑ {parent.title}</button>}{viewedMapId&&<button type="button" onClick={()=>setViewedMapId("")}>ตำแหน่งปัจจุบัน</button>}</nav>}
          {map&&map.layers.length>1&&<nav className="scene-map-navigation" aria-label="ชั้นแผนที่">
            {layer?.parentLayerId&&<button type="button" onClick={()=>{setViewedLayerId(layer.parentLayerId!);setZoomLevel(1);}}>↑ ชั้นหลัก</button>}
            <select aria-label="ดูชั้นแผนที่" value={layer?.id??''} onChange={event=>{setViewedLayerId(event.target.value);setZoomLevel(1);}}>{map.layers.map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</select>
            {viewedLayerId&&viewedLayerId!==layerId&&<button type="button" onClick={()=>{setViewedLayerId('');setZoomLevel(1);}}>ตำแหน่งปัจจุบัน</button>}
          </nav>}
          {readableMaps.some(item=>item.id!==map?.id&&item.parentMapId===map?.id)&&<nav className="scene-map-navigation" aria-label="ดูแผนที่ย่อย">{readableMaps.filter(item=>item.id!==map?.id&&item.parentMapId===map?.id).map(child=><button type="button" key={child.id} onClick={()=>setViewedMapId(child.id)}>{child.title}</button>)}</nav>}
          <div className="scene-map-viewport">
            {map ? (
              <div
                className="scene-map-canvas"
                style={{ width: `min(100%, ${220 * imageRatio}px)`, aspectRatio: imageRatio, transform: `scale(${zoomLevel})`, transition: "transform 0.2s ease" }}
              >
                {mapImage && <AccountMediaImage source={mapImage} alt={map.title} onLoad={event => {
                  const image = event.currentTarget;
                  if (image.naturalWidth > 0 && image.naturalHeight > 0) setImageSize({ source: mapImage, ratio: image.naturalWidth / image.naturalHeight });
                }} />}
                {!mapImage && !["queued", "generating"].includes(String(map.imageGeneration)) && <span className="scene-map-empty-image">ยังไม่มีภาพของชั้นแผนที่นี้</span>}
                <svg className="scene-map-areas" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">{areas.map(area=><polygon key={area.id} points={area.vertices.map(vertex=>`${vertex.x*100},${vertex.y*100}`).join(' ')} fill={area.style?.fillColor||'currentColor'} fillOpacity={area.style?.fillOpacity??.12} stroke={area.style?.strokeColor||'currentColor'} strokeWidth={area.style?.strokeWidth??.3}/>)}</svg>
                {!mapImage && ["queued","generating"].includes(String(map.imageGeneration)) && <span className="receipt-image-spinner" role="status" aria-label="กำลังสร้างแผนที่"/>}
                {anchors.map(anchor=><button type="button" key={anchor.id} className="scene-map-anchor" style={{left:`${anchor.x*100}%`,top:`${anchor.y*100}%`}} title={anchor.label} onClick={()=>openLocation(anchor.locationDocumentId)}>⌖<span>{anchor.label}</span></button>)}
                {tokens.map(token => <button type="button" key={token.id} className="scene-map-token" style={{ left: `${token.x * 100}%`, top: `${token.y * 100}%`, backgroundColor: token.style?.color || "var(--pf-primary)" }} title={token.label} onClick={() => token.link&&openLocation(token.link)}>{token.style?.icon || "●"}<span>{token.label}</span></button>)}
              </div>
            ) : (
              <div className="scene-map-placeholder">
                <span>📍</span>
                <p>{"ยังไม่มีแผนที่ของฉากนี้"}</p>
              </div>
            )}

            {/* Map title overlay at bottom-left */}
            <div className="map-caption-badge">
              <strong>{map?.title || "แผนที่ฉาก"}</strong>
            </div>

            {/* Map zoom controls */}
            <div className="map-hud-controls">
              <button
                type="button"
                className="map-control-btn"
                onClick={() => setZoomLevel((z) => Math.min(2.5, z + 0.25))}
                title="Zoom in"
              >
                +
              </button>
              <button
                type="button"
                className="map-control-btn"
                onClick={() => setZoomLevel((z) => Math.max(0.75, z - 0.25))}
                title="Zoom out"
              >
                -
              </button>
              <button
                type="button"
                className="map-control-btn"
                onClick={() => setZoomLevel(1)}
                title="Reset zoom"
              >
                ⊙
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GmPinnedFileModule({
  activeLocation,
  onSelectEntity,
}: {
  activeLocation?: WorldDocument | undefined;
  onSelectEntity?: ((doc: WorldDocument) => void) | undefined;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const image = activeLocation?.fields?.image || activeLocation?.fields?.imageUrl;

  return (
    <div className={`craft-sidebar-module gm-pinned-module frosted-panel ${collapsed ? "collapsed" : ""}`}>
      <div className="module-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="module-header__title">
          <span className="module-icon">📌</span>
          <strong>GM PINNED FILE</strong>
          {activeLocation && <small className="header-subtitle">{activeLocation.title}</small>}
        </div>
        <div className="module-header__actions" onClick={(e) => e.stopPropagation()}>
          {activeLocation && onSelectEntity && (
            <button
              type="button"
              className="popout-btn"
              title="Open file"
              onClick={() => onSelectEntity(activeLocation)}
            >
              ↗
            </button>
          )}
          <button
            type="button"
            className="collapse-toggle-btn"
            onClick={() => setCollapsed(!collapsed)}
            aria-label="Toggle collapse"
          >
            {collapsed ? "˅" : "˄"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="module-body gm-pinned-body">
          {image ? (
            <div className="pinned-image-banner">
              <AccountMediaImage source={String(image)} alt={activeLocation?.title || "Pinned file"} />
              <div className="banner-shade" />
              <div className="banner-info">
                <strong>{activeLocation?.title}</strong>
                <p>{activeLocation?.summary || ""}</p>
              </div>
            </div>
          ) : (
            <div className="pinned-text-card">
              <h4>{activeLocation?.title || ""}</h4>
              <p>{activeLocation?.summary || "GM ได้ปักหมุดข้อมูลสถานที่หรือภารกิจหลักนี้ไว้เพื่อเป็นข้อมูลอ้างอิง"}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PinnedEntityCard({
  doc,
  onSelectEntity,
}: {
  doc: WorldDocument;
  onSelectEntity?: ((doc: WorldDocument) => void) | undefined;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const image = typeof doc.fields?.image === "string" ? doc.fields.image : typeof doc.fields?.imageUrl === "string" ? doc.fields.imageUrl : "";

  return (
    <div className={`craft-sidebar-module pinned-entity-card frosted-panel ${collapsed ? "collapsed" : ""}`}>
      <div className="module-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="module-header__title">
          <span className="module-icon">
            {doc.kind === "item" || doc.kind === "weapon" ? "🗡" : doc.kind === "location" ? "🗺" : doc.kind === "faction" ? "🛡" : "📄"}
          </span>
          <strong>{doc.title.toUpperCase()}</strong>
          <span className="entity-kind-badge">{doc.kind}</span>
        </div>
        <div className="module-header__actions" onClick={(e) => e.stopPropagation()}>
          {onSelectEntity && (
            <button
              type="button"
              className="popout-btn"
              title="Inspect entity"
              onClick={() => onSelectEntity(doc)}
            >
              ↗
            </button>
          )}
          <button
            type="button"
            className="collapse-toggle-btn"
            onClick={() => setCollapsed(!collapsed)}
            aria-label="Toggle collapse"
          >
            {collapsed ? "˅" : "˄"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="module-body pinned-entity-body">
          {image && (
            <div className="entity-thumb-box">
              <AccountMediaImage source={image} alt={doc.title} />
            </div>
          )}
          {doc.summary && <p className="entity-summary">{doc.summary}</p>}
          {doc.fields && Object.keys(doc.fields).length > 0 && (
            <div className="entity-fields-grid">
              {Object.entries(doc.fields)
                .filter(([k]) => k !== "image" && k !== "imageUrl" && k !== "avatar")
                .slice(0, 6)
                .map(([key, val]) => (
                  <div key={key} className="entity-field-item">
                    <span className="field-label">{key}</span>
                    <strong className="field-val">{String(val)}</strong>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main InChatRightDrawer Component
// -----------------------------------------------------------------------------

export function InChatRightDrawer({
  isOpen,
  onClose,
  activeTab: externalTab,
  onTabChange,
  character,
  activeLocation,
  documents: allDocuments,
  session,
  models,
  selectedModel,
  onSelectModel,
  imageModel, imageEnabled, onSelectImageModel, onToggleImageGeneration,
  accessToken,
  onRunCopilot,
  onSelectEntity,
  contextPacket,
  contextManifest,
  agentActions = [],
  worldEvents = [],
  onUndoWorldTransaction = () => {},
  onUpdateSession = () => {},
  onSaveDefaults = () => {},
  onUploadMedia,
  sidebarConfig,
  onOpenCustomizeModal,
}: InChatRightDrawerProps) {
  const documents = playerVisibleDocuments(allDocuments);
  const [copilotReceipts, setCopilotReceipts] = useState<ActionReceipt[]>([]);
  const [internalTab, setInternalTab] = useState<"overview" | "assistant">("overview");
  const activeTab = externalTab ?? internalTab;

  const handleTabChange = (tab: "overview" | "assistant") => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalTab(tab);
    }
  };

  // Orbit Assistant Chat State
  const [assistantMessages, setAssistantMessages] = useState<OrbitChatMessage[]>([]);
  const orbitAbort = useRef<AbortController | null>(null);
  useEffect(() => () => orbitAbort.current?.abort(), []);
  const [orbitInput, setOrbitInput] = useState("");
  const [orbitGenerating, setOrbitGenerating] = useState(false);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeTab === "assistant") {
      chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [assistantMessages, activeTab]);


  const sendOrbitMessage = async (textToSend?: string) => {
    const query = (textToSend || orbitInput).trim();
    if (!query || orbitGenerating) return;

    const userMsg: OrbitChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: query,
      timestamp: Date.now(),
    };

    const assistantMsgId = `assistant-${Date.now()}`;
    const placeholderMsg: OrbitChatMessage = {
      id: assistantMsgId,
      role: "assistant",
      content: "",
      timestamp: Date.now(),
    };

    setAssistantMessages((prev) => [...prev, userMsg, placeholderMsg]);
    setOrbitInput("");
    setOrbitGenerating(true);

    setCopilotReceipts([]);
    orbitAbort.current = new AbortController();
    try {
      if (!onRunCopilot) throw new Error("เข้าสู่ระบบเพื่อใช้ Movis");
      const response = await onRunCopilot(query, assistantMessages.filter((m): m is OrbitChatMessage & { role: "user" | "assistant" } => m.role === "user" || m.role === "assistant").slice(-8).map(m => ({ role: m.role, content: m.content })), receipt => setCopilotReceipts(current => [...current, receipt]), orbitAbort.current.signal);
      setAssistantMessages(current => current.map(m => m.id === assistantMsgId ? { ...m, content: normalizeVisibleNarration(response) } : m));
    } catch (error) {
      setAssistantMessages(current => current.map(m => m.id === assistantMsgId ? { ...m, content: `ทำงานไม่สำเร็จ: ${error instanceof Error ? error.message : String(error)}` } : m));
    } finally {
      setOrbitGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <aside className="in-chat-right-drawer frosted-panel">
      {/* Top Header with 3 Tabs & Customize Gear Button */}
      <div className="in-chat-drawer__header">
        <SlidingTabs value={activeTab} onChange={handleTabChange} label="Overview / Movis" items={[{id:'overview',label:'◫ Overview'},{id:'assistant',label:<><MovisAvatar size={20} glowing={false} floating={false} /> Movis</>}]} />

        <div className="drawer-header-actions">
          <button
            type="button"
            className="drawer-customize-gear-btn"
            onClick={onOpenCustomizeModal}
            title="Customize sidebar"
            aria-label="Customize sidebar"
          >
            ⚙
          </button>
          <button
            type="button"
            className="drawer-close-btn"
            onClick={onClose}
            aria-label="ปิดแถบด้านข้าง"
          >
            ×
          </button>
        </div>
      </div>

      {/* Content Body */}
      <div className="in-chat-drawer__body">
        {activeTab === "overview" ? (
          <div className="overview-tab-content">
            {[...new Set([...documents.filter(doc => doc.runtimeState?.sidebarPinned === true).map(doc => doc.id), ...sidebarConfig.order])].map((itemId) => {
              if (itemId === "music_player") {
                return null;
              }
              if (itemId === "scene_map") {
                return (
                  <SceneMapModule
                    key="scene_map"
                    {...activeSceneMap(documents,session?.storyMaps,session?.activeMapId)}
                    activeLocation={activeLocation}
                    documents={documents}
                    onSelectEntity={onSelectEntity}
                  />
                );
              }
              if (itemId === "gm_pinned_file") return null;
              if (itemId === "character_sheet") return character && documents.some(doc => doc.id === character.id) ? <StoryFileView key="character_sheet" document={documents.find(doc => doc.id === character.id)!} documents={documents} session={session} onSelectEntity={onSelectEntity} /> : null;
              const doc = documents.find(d => d.id === itemId);
              if (doc) return <StoryFileView key={doc.id} document={doc} documents={documents} session={session} onSelectEntity={onSelectEntity} />;

              return null;
            })}

            {sidebarConfig.order.length === 0 && (
              <div className="overview-empty-state">
                <p>ยังไม่มีการปักหมุดโมดูลใน Sidebar</p>
                <button
                  type="button"
                  className="primary"
                  onClick={onOpenCustomizeModal}
                >
                  ⚙ ปรับแต่ง Sidebar
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="assistant-tab-content">
            {/* In-Chat Assistant Conversation Scroll Area */}
            <ActionReceipts currentFileFields={new Map(documents.map(doc=>[doc.id,doc.fields]))} receipts={copilotReceipts} working={orbitGenerating} onOpenFile={id => { const doc = documents.find(doc => doc.id === id); if (doc) { onSelectEntity?.(doc); } }} />
            <div className="assistant-conversation-feed">
              {assistantMessages.length === 0 && <div className="movis-empty-icon"><MovisAvatar size={72} glowing floating={false} /></div>}
              {assistantMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`assistant-msg-bubble ${msg.role === "assistant" ? "agent" : "user"}`}
                >
                  {msg.role === "assistant" && (
                    <div className="agent-avatar-tag">
                      <span>Movis</span>
                    </div>
                  )}
                  <div className="msg-text">{normalizeVisibleNarration(msg.content)}</div>
                </div>
              ))}
              {orbitGenerating && (
                <div className="assistant-msg-bubble agent loading">
                  <div className="agent-avatar-tag">
                    <span>Movis</span>
                  </div>
                  <div className="movis-processing-icon is-working" role="status" aria-label="Movis กำลังประมวลผล"><MovisAvatar size={24} glowing={false} floating={false} /></div>
                </div>
              )}
            </div>

              {/* Assistant Input Bar */}
              <div className="assistant-composer-wrap">
                <form
                  className="assistant-composer-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendOrbitMessage();
                  }}
                >
                  <textarea
                    rows={2}
                    onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) { event.preventDefault(); void sendOrbitMessage(); } }}
                    placeholder={orbitGenerating ? "Movis Agent กำลังประมวลผล..." : "ถามหรือสั่ง Movis Agent เกี่ยวกับโลกนี้..."}
                    value={orbitInput}
                    onChange={(e) => setOrbitInput(e.target.value)}
                    disabled={orbitGenerating}
                  />
                  <div className="assistant-composer-footer"><ModelPicker models={models} selectedId={selectedModel} onSelect={onSelectModel} selectedImageModelId={imageModel} onSelectImageModel={onSelectImageModel} imageGenerationEnabled={imageEnabled} onToggleImageGeneration={onToggleImageGeneration} />
                  <button
                    type="submit"
                    className="assistant-send-btn"
                    disabled={orbitGenerating || !orbitInput.trim()}
                  >
                    {orbitGenerating ? "..." : "↑"}
                  </button></div>
                </form>
              </div>
          </div>
        )}
      </div>
    </aside>
  );
}
